import config from 'universal-config';
import * as logger from 'universal-logger';
import { initialize, SubmissionError } from 'redux-form';
import { createFormAction } from 'redux-form-saga';
import { all, call, put, select, spawn, takeEvery } from 'redux-saga/effects';
import { getUserLocation } from 'redux/saga/helpers';
import { getSelectorHtml } from 'helpers/getSelectorHtml';
import { friendbuyCode as getFriendbuyCode } from 'redux/saga/selectors/friendbuy';
import { selectUserLocale } from 'redux/modules/locale';
import { deepAssign, deepExtract } from 'helpers/deep';
import { ga } from 'helpers/multitrack';
import { segment } from 'helpers/segment';
import { getForApi as getUtmValues } from 'helpers/utmValues';
import { loadAccountCreationConsent, transformConsentForApi } from 'components/Consent/Consent.saga';
import { GENERIC_ERROR } from 'components/Errors/ApiError';
import { createUserAndLogIn } from 'features/Login/Authentication.saga';
import { postSignupTracking } from 'features/Signup/Signup.saga';

export const ACCOUNT_FORM = 'signup-card-account';
export const accountFormAction = createFormAction(ACCOUNT_FORM);

export const initializeAccountForm = hiddenFields => ({
  type: `INITIALIZE_${ACCOUNT_FORM}`,
  hiddenFields,
});

export function* buildAccountDetails(client, form) {
  const renderedTerms = yield call(getSelectorHtml, '.SignupCard__terms__text > span');
  const [activeLocation, consents, conversion, friendbuyCode, locale] = yield all([
    call(getUserLocation, client),
    call(transformConsentForApi, { ...form, terms: renderedTerms }),
    call(getUtmValues),
    select(getFriendbuyCode),
    select(selectUserLocale),
  ]);

  // Ensure RAF leads are correctly tracked
  if (friendbuyCode) {
    deepAssign('utm.utm_term', conversion, friendbuyCode);
  }

  // NOTE: changed through nav language selection
  const user = {
    email: form.email,
    first_name: form.firstName,
    last_name: form.lastName,
    locale,
  };

  return {
    client_id: config().clientId,
    consents,
    conversion,
    newsletter_subscribed: (form.marketingOptOutDisclaimer && !form.marketingOptOut) || form.marketing,
    gls_location_id: deepExtract('location.id', activeLocation, null),
    user,
  };
}

export function buildProduct(form) {
  const productKey = {
    offer: 'msa_offer_id',
    plan: 'plan_id',
  }[form.productType];

  return productKey ? { [productKey]: form.productId } : {};
}

export function* createAccount(client, accountDetails, product) {
  // NOTE: currently all fixable errors are resolved in prior phases
  return yield call(createUserAndLogIn, client, accountFormAction, {
    ...accountDetails,
    ...product,
  });
}

export function* doInitializeAccountForm(client, { hiddenFields }) {
  const consent = yield call(loadAccountCreationConsent, client);
  yield put(
    initialize(ACCOUNT_FORM, {
      ...consent,
      ...hiddenFields,
      terms: consent.accountDisclaimer,
    })
  );
}

export function* doSubmitAccountForm(client, { payload: form }) {
  try {
    const accountDetails = yield call(buildAccountDetails, client, form);
    const product = yield call(buildProduct, form);
    const userId = yield call(createAccount, client, accountDetails, product);
    yield call(postSignupTracking, 'email', accountDetails.user, userId);
    yield call(segment.track, 'Membership Purchase', {
      action: 'Membership Purchase Account Creation',
    });
    yield call(ga, 'Membership Purchase', 'Membership Purchase Account Creation');
    yield put(accountFormAction.success());
  } catch (error) {
    yield call(segment.track, 'Membership Purchase', {
      action: 'Membership Purchase Account Creation Error',
      label: error || 'Unknown Error',
    });
    yield call(ga, 'Membership Purchase', 'Membership Purchase Account Creation Error', error || 'Unknown Error');
    yield spawn(logger.error, deepExtract('message', error, error));
    yield put(accountFormAction.failure(new SubmissionError({ _error: GENERIC_ERROR })));
  }
}

export function* watchAccountFormSagas(client) {
  yield takeEvery(initializeAccountForm().type, doInitializeAccountForm, client);
  yield takeEvery(accountFormAction.REQUEST, doSubmitAccountForm, client);
}
